import type { JSX } from 'react';

/**
 * THE BRAND MARKS - the founder's own artwork.
 *
 * `brand/croissant-in-bed.png` IS `assets/croissantinbed-logo-light.jpeg`, the
 * design reference, prepared for shipping by `assets/make-brand-mark.py`: cropped square
 * to the stamp, and its cream field converted to alpha over one flat `--cib-ink`
 * so the grunge texture survives and the ground does not. The components below
 * used to be a redraw of that stamp in SVG primitives, kept only until the
 * artwork arrived; the redraw is gone.
 *
 * The reference file is opaque, and that is the whole reason the mark is derived
 * rather than shipped as-is: a rectangle of JPEG cream would show its edges
 * against any header, and the JPEG's ground is not even one colour (it drifts
 * between #f8e8d8 and #f9ead7 and carries a green chroma fringe down its right
 * edge). Alpha removes the seam outright. `--cib-topbar-bg` still paints the
 * banner in the mark's own cream, which is what the mark was drawn to sit on.
 *
 * One light mark on every surface. There is no dark variant and no theme-based
 * switching (PRD WEB-015).
 */

/*
 * `new URL(..., import.meta.url)` rather than an import: it is the one form that
 * needs no `*.png` module declaration, so `packages/ui` keeps `"types": []` and
 * every app that compiles these sources keeps typechecking without one.
 */
const MARK_URL = new URL('./brand/croissant-in-bed.png', import.meta.url).href;

export interface LogoProps {
  /** Rendered size in px. The mark is square and scales cleanly at any value. */
  size?: number;
  /** Accessible name; set to null for a decorative mark beside visible text. */
  title?: string | null;
  className?: string;
}

/**
 * The stamp: the full circular mark. Use it where the brand appears on its own -
 * the guest-facing QR panel (PRD WEB-021), the favicon, the Open Graph image.
 */
export function LogoStamp({
  size = 96,
  title = 'Croissant in Bed',
  className,
}: LogoProps): JSX.Element {
  const labelled = title !== null;
  return (
    <img
      className={['cib-logo', 'cib-logo__mark', className].filter(Boolean).join(' ')}
      src={MARK_URL}
      width={size}
      height={size}
      /* Empty alt plus aria-hidden, not a missing alt: beside visible words the
         mark is decoration, and naming it reads the brand out twice. */
      alt={labelled ? title : ''}
      aria-hidden={labelled ? undefined : true}
      decoding="async"
      draggable={false}
    />
  );
}

export interface WordmarkProps extends LogoProps {
  /** Stacks the mark above the words instead of setting them side by side. */
  stacked?: boolean;
}

/**
 * The horizontal wordmark: the stamp beside the words. This is the lockup used in
 * app headers, the partner dashboard and the public site (PRD WEB-015).
 */
export function Wordmark({
  size = 40,
  title = 'Croissant in Bed',
  stacked = false,
  className,
}: WordmarkProps): JSX.Element {
  return (
    <span
      className={['cib-logo', className].filter(Boolean).join(' ')}
      style={{
        display: 'inline-flex',
        flexDirection: stacked ? 'column' : 'row',
        alignItems: 'center',
        gap: stacked ? 8 : 12,
      }}
    >
      <LogoStamp size={size} title={null} />
      <span
        className="cib-logo__wordmark-text"
        style={{
          fontSize: Math.round(size * 0.46),
          fontWeight: 600,
          letterSpacing: '0.01em',
          lineHeight: 1.1,
          whiteSpace: 'nowrap',
        }}
      >
        {title ?? 'Croissant in Bed'}
      </span>
    </span>
  );
}
